/**
 * The wing radial menu and the wing status (M6 plan P31, spec §6.2): the pure parts (the items, the sector pick, the
 * open/aim/release state machine) and the DOM view, which writes only what changed (the M1 HUD rule).
 *
 * - **Items.** Eight sectors clockwise from the top: the six wing commands (Attack my target, Break and engage, Defend
 *   me, Dock/return, Hold position, Form up) and the two formation choices (wedge, line; each also orders Form up).
 * - **Open, aim, release.** Y pressed (or the pad's D-pad ↑ held `wingMenuHoldSeconds`) opens it; while the key or
 *   button stays down the mouse virtual stick (or the pad's right stick) highlights a sector beyond `selectDeadzone`;
 *   releasing gives the highlighted order, or nothing when none is highlighted. Esc, a modal, a hold (docked) or the
 *   pause closes it without an order.
 *
 * Everything per frame is allocation-free (D-014): strings are precomputed and the view compares cached values.
 */
import { z } from 'zod'
import { FORMATION_NAMES, type FormationName } from '../../wing/formation'
import { WING_CMD_NAMES, type WingCommandName } from '../../wing/wing'
import wingHudJson from './wingHud.json'

export const wingHudConfigSchema = z.strictObject({
  comment: z.string(),
  selectDeadzone: z.number().gt(0).lt(1),
  radiusPx: z.number().positive(),
})
export type WingHudConfig = z.infer<typeof wingHudConfigSchema>

export function loadWingHudConfig(raw: unknown = wingHudJson): WingHudConfig {
  return wingHudConfigSchema.parse(raw)
}

export interface RadialItem {
  /** Stable id (test ids, `data-selected`). */
  readonly id: string
  readonly label: string
  readonly command: WingCommandName
  /** The formation a formation choice sets (it also orders Form up); undefined keeps the current one. */
  readonly formation: FormationName | undefined
}

/** The radial's sectors, clockwise from the top. */
export const RADIAL_ITEMS: readonly RadialItem[] = [
  { id: 'attack', label: 'Attack my target', command: 'attack', formation: undefined },
  { id: 'breakEngage', label: 'Break and engage', command: 'breakEngage', formation: undefined },
  { id: 'defend', label: 'Defend me', command: 'defend', formation: undefined },
  { id: 'wedge', label: 'Wedge formation', command: 'formUp', formation: 'wedge' },
  { id: 'dock', label: 'Dock / return', command: 'dock', formation: undefined },
  { id: 'line', label: 'Line formation', command: 'formUp', formation: 'line' },
  { id: 'hold', label: 'Hold position', command: 'hold', formation: undefined },
  { id: 'formUp', label: 'Form up', command: 'formUp', formation: undefined },
]

/** The status line's command labels, by `WING_CMD` code. */
export const COMMAND_LABELS: Readonly<Record<WingCommandName, string>> = {
  formUp: 'Form up',
  attack: 'Attacking',
  defend: 'Defending',
  hold: 'Holding',
  breakEngage: 'Engaging',
  dock: 'Returning',
}
const COMMAND_TEXT: readonly string[] = WING_CMD_NAMES.map((n) => COMMAND_LABELS[n])
const FORMATION_TEXT: readonly string[] = FORMATION_NAMES.map((n) => (n === 'wedge' ? 'Wedge' : 'Line'))

const TWO_PI = Math.PI * 2

/**
 * The radial sector `(x, y)` points at (x right, y up; 0 = the top, clockwise), or −1 inside `deadzone`.
 * Allocation-free.
 */
export function radialSector(x: number, y: number, deadzone: number, sectors: number = RADIAL_ITEMS.length): number {
  if (x * x + y * y < deadzone * deadzone) return -1
  const k = Math.round(Math.atan2(x, y) / (TWO_PI / sectors))
  return ((k % sectors) + sectors) % sectors
}

/** What holds the radial open. */
export const RADIAL_SOURCE = { none: 0, key: 1, pad: 2 } as const
export type RadialSource = (typeof RADIAL_SOURCE)[keyof typeof RADIAL_SOURCE]

export interface RadialState {
  source: RadialSource
  /** The highlighted item, −1 when none. */
  selected: number
}

export function createRadialState(): RadialState {
  return { source: RADIAL_SOURCE.none, selected: -1 }
}

/** One frame's input to the radial (filled in place by the feature). */
export interface RadialInput {
  /** The `wingMenu` edge fired this frame (Y pressed, or D-pad ↑ held long enough). */
  opened: boolean
  /** The wing-menu key (Y) is down. */
  keyHeld: boolean
  /** The pad's D-pad ↑ (`toggleFA`) is down. */
  padHeld: boolean
  /** The mouse virtual stick (x right, y up). */
  mouseX: number
  mouseY: number
  /** The pad's aim stick (x right, y up). */
  padX: number
  padY: number
  /** Esc: close without an order. */
  cancel: boolean
  /** A modal, a hold or no wing: the radial may not be open. */
  blocked: boolean
}

export function createRadialInput(): RadialInput {
  return { opened: false, keyHeld: false, padHeld: false, mouseX: 0, mouseY: 0, padX: 0, padY: 0, cancel: false, blocked: false }
}

function close(s: RadialState): void {
  s.source = RADIAL_SOURCE.none
  s.selected = -1
}

/**
 * Steps the radial one frame. Returns the index of the item ordered this frame (on the release), −1 otherwise.
 * Allocation-free.
 */
export function stepRadial(s: RadialState, inp: Readonly<RadialInput>, deadzone: number): number {
  if (s.source === RADIAL_SOURCE.none) {
    if (!inp.opened || inp.blocked || (!inp.keyHeld && !inp.padHeld)) return -1
    s.source = inp.keyHeld ? RADIAL_SOURCE.key : RADIAL_SOURCE.pad
    s.selected = -1
    // Aiming starts next frame: the stick still holds the flight input (the feature centres it on opening).
    return -1
  }
  if (inp.blocked || inp.cancel) {
    close(s)
    return -1
  }
  const key = s.source === RADIAL_SOURCE.key
  if (key ? inp.keyHeld : inp.padHeld) {
    s.selected = key ? radialSector(inp.mouseX, inp.mouseY, deadzone) : radialSector(inp.padX, inp.padY, deadzone)
    return -1
  }
  // Released: the order last highlighted (the pad stick may spring back on the same frame).
  const picked = s.selected
  close(s)
  return picked
}

// -------------------------------------------------------------------------------------------------
// The view
// -------------------------------------------------------------------------------------------------

/** The element surface the view writes (real DOM elements satisfy it; tests pass counting fakes). */
export interface WingHudEl {
  className: string
  textContent: string | null
  readonly dataset: Record<string, string | undefined>
}

export interface WingHudElements {
  /** The status block (pips, command, formation). */
  status: WingHudEl
  /** One pip per possible member (`Wing.max`). */
  pips: readonly WingHudEl[]
  command: WingHudEl
  formation: WingHudEl
  radial: WingHudEl
  /** One per `RADIAL_ITEMS`. */
  items: readonly WingHudEl[]
  /** The centre label (the highlighted order). */
  centre: WingHudEl
}

/** What the view shows this frame (filled in place by the feature). */
export interface WingHudSnapshot {
  /** A wing exists (the Reach runs). */
  live: boolean
  count: number
  cap: number
  /** `WING_CMD` code and `FORMATION` code. */
  command: number
  formation: number
  radialOpen: boolean
  selected: number
}

export function createWingHudSnapshot(): WingHudSnapshot {
  return { live: false, count: 0, cap: 0, command: 0, formation: 0, radialOpen: false, selected: -1 }
}

const STATUS_SHOWN = 'wh-status'
const STATUS_HIDDEN = 'wh-status wh-status--hidden'
const PIP_ON = 'wh-pip wh-pip--on'
const PIP_EMPTY = 'wh-pip'
const PIP_NONE = 'wh-pip wh-pip--none'
const RADIAL_OPEN = 'wh-radial wh-radial--open'
const RADIAL_CLOSED = 'wh-radial'
const ITEM = 'wh-item'
const ITEM_SELECTED = 'wh-item wh-item--selected'
const CENTRE_IDLE = 'Wing orders'
const DIGITS: readonly string[] = Array.from({ length: 33 }, (_, i) => String(i))

function digits(n: number): string {
  return DIGITS[n] ?? String(n)
}

export interface WingHudView {
  sync(s: Readonly<WingHudSnapshot>): void
}

/** The view over `els`: every write is guarded by a cached value, so an unchanged frame writes nothing. */
export function createWingHudView(els: WingHudElements): WingHudView {
  let shown: boolean | null = null
  let count = -1
  let cap = -1
  let command = -1
  let formation = -1
  let open: boolean | null = null
  let selected = -2
  return {
    sync(s: Readonly<WingHudSnapshot>): void {
      const show = s.live && (s.count > 0 || s.radialOpen)
      if (show !== shown) {
        shown = show
        els.status.className = show ? STATUS_SHOWN : STATUS_HIDDEN
      }
      if (s.count !== count || s.cap !== cap) {
        count = s.count
        cap = s.cap
        for (let i = 0; i < els.pips.length; i++) els.pips[i].className = i < s.count ? PIP_ON : i < s.cap ? PIP_EMPTY : PIP_NONE
        els.status.dataset.count = digits(s.count)
        els.status.dataset.cap = digits(s.cap)
      }
      if (s.command !== command) {
        command = s.command
        els.command.textContent = COMMAND_TEXT[s.command] ?? ''
        els.status.dataset.command = WING_CMD_NAMES[s.command] ?? ''
      }
      if (s.formation !== formation) {
        formation = s.formation
        els.formation.textContent = FORMATION_TEXT[s.formation] ?? ''
        els.status.dataset.formation = FORMATION_NAMES[s.formation] ?? ''
      }
      const isOpen = s.live && s.radialOpen
      if (isOpen !== open) {
        open = isOpen
        els.radial.className = isOpen ? RADIAL_OPEN : RADIAL_CLOSED
      }
      const sel = isOpen ? s.selected : -1
      if (sel !== selected) {
        if (selected >= 0 && selected < els.items.length) els.items[selected].className = ITEM
        if (sel >= 0 && sel < els.items.length) els.items[sel].className = ITEM_SELECTED
        selected = sel
        els.centre.textContent = sel >= 0 ? RADIAL_ITEMS[sel].label : CENTRE_IDLE
        els.radial.dataset.selected = sel >= 0 ? RADIAL_ITEMS[sel].id : ''
      }
    },
  }
}

function div(doc: Document, cls: string, parent: HTMLElement, text = ''): HTMLDivElement {
  const e = doc.createElement('div')
  e.className = cls
  if (text !== '') e.textContent = text
  parent.appendChild(e)
  return e
}

/** Builds `#wing-hud` (the status and the radial) under `parent`; `pips` = the wing's largest size. */
export function mountWingHud(parent: HTMLElement, cfg: WingHudConfig, pips: number): { els: WingHudElements; host: HTMLElement } {
  const doc = parent.ownerDocument
  const host = div(doc, 'wh-root', parent)
  host.id = 'wing-hud'
  const status = div(doc, STATUS_HIDDEN, host)
  status.dataset.testid = 'wing-status'
  const head = div(doc, 'wh-status__head', status, 'Wing')
  const pipRow = div(doc, 'wh-pips', head)
  const pipEls: HTMLDivElement[] = []
  for (let i = 0; i < pips; i++) pipEls.push(div(doc, PIP_NONE, pipRow))
  const command = div(doc, 'wh-status__command', status)
  const formation = div(doc, 'wh-status__formation', status)
  const radial = div(doc, RADIAL_CLOSED, host)
  radial.dataset.testid = 'wing-radial'
  radial.dataset.selected = ''
  const items: HTMLDivElement[] = []
  for (let i = 0; i < RADIAL_ITEMS.length; i++) {
    const it = RADIAL_ITEMS[i]
    const a = (i / RADIAL_ITEMS.length) * TWO_PI
    const e = div(doc, ITEM, radial, it.label)
    e.dataset.testid = `wing-radial-${it.id}`
    e.style.transform = `translate(-50%, -50%) translate(${(Math.sin(a) * cfg.radiusPx).toFixed(1)}px, ${(-Math.cos(a) * cfg.radiusPx).toFixed(1)}px)`
    items.push(e)
  }
  const centre = div(doc, 'wh-centre', radial, CENTRE_IDLE)
  return { els: { status, pips: pipEls, command, formation, radial, items, centre }, host }
}
